/**
 * useTimelineState - Timeline State Management Hook
 *
 * Manages timeline state, undo/redo history, and computed values.
 * Extracted from Studio.tsx for better separation of concerns.
 */

import { useState, useMemo, useCallback, useRef } from 'react';
import useLocalStorage from './useLocalStorage';
import { TimelineState, Track } from '../types/timeline';

// === DEFAULT TIMELINE STATE ===
const defaultTimelineState: TimelineState = {
    project: null,
    tracks: [
        { id: 'v1', type: 'video', name: 'V1', order: 0, locked: false, muted: false, visible: true, height: 56 },
        { id: 'a1', type: 'audio', name: 'A1', order: 1, locked: false, muted: false, visible: true, height: 32 },
    ],
    segments: [],
    selectedSegmentIds: [],
    selectedTrackId: 'v1',
    expandedSegmentIds: [],
    generationQueue: [],
    playheadSec: 0
};

// === HISTORY CONFIG ===
const MAX_HISTORY = 50;

export const useTimelineState = () => {
    // === CORE TIMELINE STATE (Persisted) ===
    const [timelineState, setTimelineState] = useLocalStorage<TimelineState>(
        'studio_timeline_state',
        defaultTimelineState
    );

    // === UI STATE ===
    const [selectedGap, setSelectedGap] = useState<{
        trackId: string;
        inSec: number;
        duration: number;
    } | null>(null);

    const [iaPanelTab, setIaPanelTab] = useState<"prompt" | "keyframes" | "edit-image" | "edit-video" | "versions">('prompt');
    const [isVerticalToggle, setIsVerticalToggle] = useState(false);

    // === UNDO/REDO HISTORY ===
    const historyRef = useRef<{ past: TimelineState[]; future: TimelineState[] }>({
        past: [],
        future: []
    });

    /**
     * Push current state to history before making changes.
     * Call this BEFORE modifying timelineState.
     */
    const pushToHistory = useCallback(() => {
        historyRef.current.past.push(JSON.parse(JSON.stringify(timelineState)));
        if (historyRef.current.past.length > MAX_HISTORY) {
            historyRef.current.past.shift(); // Remove oldest
        }
        historyRef.current.future = []; // Clear redo stack
    }, [timelineState]);

    /**
     * Undo last timeline change.
     */
    const handleUndo = useCallback(() => {
        if (historyRef.current.past.length === 0) {
            console.log('[Undo] Nothing to undo');
            return;
        }
        const previousState = historyRef.current.past.pop()!;
        historyRef.current.future.push(JSON.parse(JSON.stringify(timelineState)));
        setTimelineState(previousState);
        console.log('[Undo] Restored previous state');
    }, [timelineState, setTimelineState]);

    /**
     * Redo last undone change.
     */
    const handleRedo = useCallback(() => {
        if (historyRef.current.future.length === 0) {
            console.log('[Redo] Nothing to redo');
            return;
        }
        const nextState = historyRef.current.future.pop()!;
        historyRef.current.past.push(JSON.parse(JSON.stringify(timelineState)));
        setTimelineState(nextState);
        console.log('[Redo] Restored next state');
    }, [timelineState, setTimelineState]);

    // === COMPUTED VALUES ===

    /**
     * Total duration of the timeline (minimum 30 seconds for display).
     */
    const totalDuration = useMemo(() => {
        if (timelineState.segments.length === 0) return 30;
        return Math.max(30, ...timelineState.segments.map(s => s.outSec));
    }, [timelineState.segments]);

    /**
     * Check if undo is available.
     */
    const canUndo = historyRef.current.past.length > 0;

    /**
     * Check if redo is available.
     */
    const canRedo = historyRef.current.future.length > 0;

    // === RETURN ALL STATE AND ACTIONS ===
    return {
        // Core state
        timelineState,
        setTimelineState,

        // UI state
        selectedGap,
        setSelectedGap,
        iaPanelTab,
        setIaPanelTab,
        isVerticalToggle,
        setIsVerticalToggle,

        // Undo/Redo
        pushToHistory,
        handleUndo,
        handleRedo,
        canUndo,
        canRedo,
        historyRef,

        // Computed
        totalDuration,
    };
};

// Type export for consumers
export type TimelineStateHook = ReturnType<typeof useTimelineState>;
